import { useTranslation } from 'react-i18next'

import { LineProviderBadge } from '#/entities/telephony'
import { useEscapeClose } from '#/shared/lib/hooks'
import { cn } from '#/shared/lib/utils'
import { CheckIcon, ChevronDownIcon } from '#/shared/ui/icons'

import type { ThreadMainline } from '../../model/conversation'
import { useActiveLine } from '../../model/use-phone-numbers'

/**
 * Static "From" line for a shared-mainline thread: the thread already lives on
 * one mainline, so there is nothing to pick — render the mainline number/label
 * in place of the personal-line dropdown trigger.
 */
export function ComposerMainlineLine({
  mainline,
}: {
  mainline: ThreadMainline
}) {
  return (
    <span className="truncate font-medium text-gray-500">
      {mainline.display}
    </span>
  )
}

/**
 * The SMS "From" line picker for the composer, split into a trigger (sits beside
 * the "From" label) and a list that expands DOWNWARD as an in-flow accordion
 * below the label — growing the composer's height. Being in-flow, it sits on the
 * composer dock's existing frosted blur (`bg-white/70 backdrop-blur-md`); it must
 * NOT add its own `backdrop-blur` (a nested backdrop-filter composites nothing).
 * Colours use `gray`/`warm` tokens the phone panel's dark theme remaps.
 */
export function ComposerFromLineTrigger({
  open,
  onToggle,
}: {
  open: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const { active, hasMultiple } = useActiveLine()
  const label = active?.display ?? t('phonePanel.linePlaceholder')

  // Nothing to switch between — just show the line.
  if (!hasMultiple) {
    return (
      <span className="flex min-w-0 items-center gap-1.5 font-medium text-gray-500">
        <span className="truncate">{label}</span>
        {active ? (
          <LineProviderBadge isImessage={active.isImessage} compact />
        ) : null}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex cursor-pointer items-center gap-1 font-medium text-gray-500"
    >
      <span className="truncate">{label}</span>
      {/* Initial only on the trigger; the list below spells the tier out. */}
      {active ? (
        <LineProviderBadge isImessage={active.isImessage} compact />
      ) : null}
      <ChevronDownIcon
        className={cn(
          'size-3 shrink-0 transition-transform duration-200',
          open && 'rotate-180',
        )}
      />
    </button>
  )
}

export function ComposerFromLineList({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  // `select` is the LOCAL per-message pick — choosing a sender here never
  // changes the account default (only the header / keypad control does).
  const { lines, active, select, hasMultiple } = useActiveLine()
  useEscapeClose(onClose)

  if (!hasMultiple) return null

  return (
    <div
      className={cn(
        'overflow-hidden transition-all duration-200 ease-out',
        open ? 'max-h-72 opacity-100' : 'pointer-events-none max-h-0 opacity-0',
      )}
    >
      {/* Plain divided list (not a card): a top rule separates it from the
          From-row, dividers separate each line. */}
      <div className="mt-2 max-h-72 divide-y divide-gray-200 overflow-y-auto border-t border-gray-200">
        {lines.map((line) => (
          <button
            key={line.id}
            type="button"
            onClick={() => {
              select(line.id)
              onClose()
            }}
            className="flex w-full cursor-pointer items-center justify-between gap-2 px-1 py-2.5 text-left transition-colors hover:bg-gray-50"
          >
            <span className="flex min-w-0 flex-col">
              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
                <span className="truncate">{line.phone}</span>
                <LineProviderBadge isImessage={line.isImessage} />
              </span>
              {line.label ? (
                <span className="truncate text-xs text-warm-500">
                  {line.label}
                </span>
              ) : null}
            </span>
            {line.id === active?.id ? (
              <CheckIcon className="size-4 shrink-0 text-blue-500" />
            ) : null}
          </button>
        ))}
      </div>
    </div>
  )
}
